import { jsx } from "react/jsx-runtime";
import FingerprintJS from "@fingerprintjs/fingerprintjs-pro";
import { customAlphabet } from "nanoid";
import React__default, { useEffect } from "react";
import { Cookies } from "react-cookie";
import { create } from "zustand";
import BrandclubStoreConfigUtils from "../utils/brandclubStoreConfigUtils.js";
import { track } from "./AnalyticsService.js";
import { AnalyticsTrackingEvent } from "./events.js";
import { timeoutPromise } from "./utils.js";
const useFingerprintJSStore = create((set) => ({
  visitorId: void 0,
  setVisitorTracking: (visitorTracking) => set({
    visitorId: visitorTracking.uniqueVisitorId ?? void 0,
    visitorSessionId: visitorTracking.visitorSessionId,
    visitorTracking
  }),
  visitorTracking: void 0,
  fpLoadPromise: void 0,
  setFpLoadPromise: (fpLoadPromise) => set({ fpLoadPromise })
}));
const nanoid = customAlphabet("1234567890abcdefghijklmnopqrstuvwxyz", 10);
const FINGERPRINT_JS_BROWSER_API_KEY = "K1ACT61coP6ep24p7Bwr";
const FingerprintJSContext = React__default.createContext(void 0);
const useFingerprintJSContext = () => React__default.useContext(FingerprintJSContext);
const DEBUG = false;
const vsCookieName = "_vs_id";
const fpResultCacheKey = `fp:${vsCookieName}:result`;
const cookies = new Cookies();
let visitorSessionId = cookies.get(vsCookieName);
if (!visitorSessionId) {
  visitorSessionId = nanoid();
  cookies.set(vsCookieName, visitorSessionId, {
    domain: "." + BrandclubStoreConfigUtils.getCookieDomain(),
    maxAge: 60 * 60 * 24,
    // 1 day
    path: "/",
    secure: true,
    httpOnly: false,
    sameSite: "lax"
  });
}
const FingerprintJSProvider = ({
  children,
  hostDomainName
}) => {
  const FINGERPRINT_JS_AGENT_ENDPOINT = hostDomainName;
  const [visitorTracking, setVisitorTracking, fpLoadPromise, setFpLoadPromise] = useFingerprintJSStore((state) => [
    state.visitorTracking,
    state.setVisitorTracking,
    state.fpLoadPromise,
    state.setFpLoadPromise
  ]);
  useEffect(() => {
    const referrer = document.referrer;
    if (!(visitorTracking == null ? void 0 : visitorTracking.uniqueVisitorId) || !(visitorTracking == null ? void 0 : visitorTracking.isResolved)) {
      let fpResultValue = window.localStorage.getItem(fpResultCacheKey);
      if (!fpResultValue) {
        fpResultValue = window.sessionStorage.getItem(fpResultCacheKey);
        if (fpResultValue) {
          window.localStorage.setItem(fpResultCacheKey, fpResultValue);
          window.sessionStorage.removeItem(fpResultCacheKey);
        }
      }
      if (fpResultValue) {
        try {
          const { visitorSessionId: cachedSessionId, result } = JSON.parse(fpResultValue);
          if (visitorSessionId === cachedSessionId) {
            setVisitorTracking({
              visitorSessionId,
              uniqueVisitorId: result.visitorId,
              referrer,
              fingerprintResult: result,
              isResolved: true
            });
            DEBUG && console.log("FingerprintJS loaded from cache", visitorSessionId);
            return;
          }
        } catch (e) {
          console.error(`Error parsing cached fingerprint result: ${e}`);
        }
      }
      if (fpLoadPromise) {
        return;
      }
      const fpLoadPromiseBuilder = () => {
        const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator == null ? void 0 : navigator.userAgent);
        if (isBot) {
          const result = {
            visitorId: "bc-bot:" + visitorSessionId,
            bot: true
          };
          setVisitorTracking({
            visitorSessionId,
            uniqueVisitorId: result.visitorId,
            referrer,
            fingerprintResult: result,
            isResolved: true
          });
          window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId, result }));
          setFpLoadPromise(void 0);
          return Promise.resolve();
        }
        return timeoutPromise(
          2e3,
          "FJs.load",
          () => FingerprintJS.load({
            scriptUrlPattern: (
              // "https://fpnpmcdn.net/v<version>/<apiKey>/loader_v<loaderVersion>.js",
              `${FINGERPRINT_JS_AGENT_ENDPOINT}/Jzq31cA2yOXuX74326KI33y/dXtUA01lo82H22P4lNPkV46S?apiKey=<apiKey>&version=3&loaderVersion=3.8.1`
            ),
            apiKey: FINGERPRINT_JS_BROWSER_API_KEY,
            // This works but goes through our proxy but FINGERPRINT_JS_BROWSER_ENDPOINT directly works as well
            endpoint: `${FINGERPRINT_JS_AGENT_ENDPOINT}/Jzq31cA2yOXuX74326KI33y/5ksfEIl7Q0t2f8es3L1oIS99`
          })
        ).then((fp) => {
          return fp.get({ extendedResult: true }).then((result) => {
            setVisitorTracking({
              visitorSessionId,
              uniqueVisitorId: result.visitorId,
              referrer,
              fingerprintResult: result,
              isResolved: true
            });
            window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId, result }));
          }).catch((e) => {
            setVisitorTracking({
              visitorSessionId,
              uniqueVisitorId: "bc-session:" + visitorSessionId,
              referrer,
              isResolved: false
            });
            track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
              error: e,
              type: "get"
            });
          });
        }).catch((e) => {
          setVisitorTracking({
            visitorSessionId,
            uniqueVisitorId: "bc-session:" + visitorSessionId,
            referrer,
            isResolved: false
          });
          track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
            type: "load",
            error: e
          });
        }).finally(() => {
          setFpLoadPromise(void 0);
        });
      };
      setFpLoadPromise(fpLoadPromiseBuilder());
    }
  }, [visitorTracking]);
  if (!visitorTracking) {
    return null;
  }
  return /* @__PURE__ */ jsx(FingerprintJSContext.Provider, { value: visitorTracking, children });
};
const useVisitorTracking = () => {
  const state = useFingerprintJSStore.getState();
  return [state.visitorTracking, state.setVisitorTracking];
};
const USER_ID_FETCH_TIMEOUT = 3e3;
const asyncGetVisitorId = () => {
  const elapsed = Date.now();
  return new Promise((resolve, reject) => {
    const checkTrackingInfo = () => {
      const [visitorTracking, setVisitorTracking] = useVisitorTracking();
      if (visitorTracking == null ? void 0 : visitorTracking.uniqueVisitorId) {
        resolve(visitorTracking.uniqueVisitorId);
        if (!visitorTracking.isResolved) {
          setVisitorTracking({ ...visitorTracking });
        }
        return;
      }
      if (Date.now() - elapsed > USER_ID_FETCH_TIMEOUT) {
        reject("Timeout waiting for visitorId");
        return;
      }
      setTimeout(checkTrackingInfo, 100);
    };
    checkTrackingInfo();
  });
};
const tryGetVisitorId = async () => {
  try {
    return await asyncGetVisitorId();
  } catch (e) {
    console.error(e);
    return void 0;
  }
};
export {
  FingerprintJSContext,
  FingerprintJSProvider,
  tryGetVisitorId,
  useFingerprintJSContext
};
//# sourceMappingURL=fingerprint.js.map
